import { printKitchenReceipts, printReceipt } from "@/helpers/printHelper";
import { ORDER_STATUS } from "@/helpers/order/status";

export const AUTOSEND_KITCHEN_ONLINE_SETTING = "autosend_kitchen_online";
const TYPE_ONLINE_ORDER = 1;
const AUTO_SEND_REASON = "online-order-auto";
const TRUTHY_SETTING_VALUES = new Set(["true", "1", "yes", "on"]);

/**
 * Whether the terminal is configured to automatically fire incoming online
 * orders to the kitchen on arrival. Defaults to false when the setting is
 * missing, mirroring the backend default.
 */
export function isOnlineKitchenAutoSendEnabled(terminal) {
  const settings = terminal && terminal.db_settings;
  if (!Array.isArray(settings)) return false;
  const setting = settings.find((item) => item && item.name === AUTOSEND_KITCHEN_ONLINE_SETTING);
  if (!setting || setting.value == null) return false;
  if (setting.value === true) return true;
  return TRUTHY_SETTING_VALUES.has(String(setting.value).trim().toLowerCase());
}

function productIdsMatch(productId, itemProductId) {
  if (productId == null || itemProductId == null) return false;
  return String(productId) === String(itemProductId);
}

// Online order payloads do not carry printer assignments, so resolve them from
// the product catalog the same way the manual Approve flow does.
function attachPrintersFromProducts(order, products) {
  (order.items || []).forEach((item) => {
    const productConfig = products.find((product) => productIdsMatch(product.id, item.product));
    if (productConfig && productConfig.printers && productConfig.printers.length > 0) {
      item.printers = productConfig.printers;
      item.has_kitchen_updates = !!productConfig.printers.length;
    }
  });
}

function collectKitchenItems(order) {
  return (order.items || []).filter(
    (item) =>
      item.printers &&
      item.printers.length > 0 &&
      (item.void_status === 0 || item.void_status === null || item.void_status === undefined) &&
      !item.sent_to_kitchen &&
      item.quantity > 0
  );
}

function stampApproval(order, { employee, terminal }) {
  order.related_terminal = terminal ? terminal.id : order.related_terminal;
  if (employee && employee.id != null) {
    order.employee = employee.id;
  }
  // Mirror the manual Approve flow: a paid order is closed, an unpaid one opens.
  if (order.payments && order.payments.length) {
    order.status = ORDER_STATUS.COMPLETED;
    order.close_date = new Date().toISOString();
  } else {
    order.status = ORDER_STATUS.OPEN;
  }
}

/**
 * Automatically send a newly arrived online order to the kitchen when the
 * `autosend_kitchen_online` setting is enabled. Mutates `order` in place with
 * the kitchen/approval flags so the caller can persist and sync it.
 *
 * @returns {Promise<{ sent: boolean, order: object, errors?: array }>}
 */
export async function autoSendOnlineOrderToKitchen({ order, products = [], terminal, employee }) {
  if (!order || order.type !== TYPE_ONLINE_ORDER) return { sent: false, order };

  // Skip orders already in a terminal state — they need no further approval.
  // We intentionally do NOT guard on receipt_kitchen_printed here: the backend
  // may set that flag when sending a payment-confirmation email/SMS before the
  // POS has had a chance to approve the order, which would silently block
  // paid-order auto-approval.
  const isTerminal = (
    order.status === ORDER_STATUS.COMPLETED ||
    order.status === ORDER_STATUS.CANCELLED ||
    order.status === ORDER_STATUS.REFUNDED
  );
  if (isTerminal) return { sent: false, order };

  if (!isOnlineKitchenAutoSendEnabled(terminal)) return { sent: false, order };

  const isPaid = !!(order.payments && order.payments.length);

  attachPrintersFromProducts(order, products);
  // collectKitchenItems already filters to !item.sent_to_kitchen, which is the
  // correct per-item idempotency check for a table order that gets more rounds
  // added over time. Do NOT also short-circuit on order.receipt_kitchen_printed
  // here (as this used to) — that flag goes true after the FIRST round and
  // stays true for the order's lifetime, which would silently skip every
  // later round's items. It's also unreliable for the reason noted above: the
  // backend may set it for a payment-confirmation receipt before the kitchen
  // has seen anything.
  const kitchenItems = collectKitchenItems(order);

  try {
    if (kitchenItems.length) {
      const printResults = await printKitchenReceipts(order, kitchenItems, AUTO_SEND_REASON);
      const printFailed = printResults && printResults.errors && printResults.errors.length;

      if (printFailed) {
        // Always approve regardless of print success — autosend_kitchen_online means
        // "approve on arrival." A printer failure must never block the order from
        // moving out of Pending; staff can reprint manually.
        console.warn("Kitchen print error on auto-send — approving anyway:", printResults.errors);
      } else {
        kitchenItems.forEach((kitchenItem) => {
          const index = order.items.findIndex(
            (item) =>
              (item.id != null && item.id === kitchenItem.id) ||
              (item.local_id != null && item.local_id === kitchenItem.local_id)
          );
          if (index !== -1) {
            order.items[index].sent_to_kitchen = true;
            order.items[index].has_kitchen_updates = false;
          }
        });
      }
    }

    // Mark order as handled and stamp approval (COMPLETED for paid, OPEN for unpaid).
    order.receipt_kitchen_printed = true;
    stampApproval(order, { employee, terminal });

    // Paid (prepaid) online orders are completed on auto-fire, so there is no
    // later checkout step to print the customer receipt. Print it now. Isolated
    // in its own try/catch so a receipt-printer failure can never block the save.
    if (isPaid) {
      try {
        await printReceipt(order);
      } catch (receiptError) {
        console.warn("Auto receipt print for paid online order failed", receiptError);
      }
    }

    return { sent: true, order };
  } catch (error) {
    // Unexpected error — always approve so a crash never leaves orders stuck in Pending.
    console.warn("Auto kitchen send for online order failed", error);
    order.receipt_kitchen_printed = true;
    stampApproval(order, { employee, terminal });
    return { sent: true, order };
  }
}
